import {useCallback,useEffect,useRef,useState} from 'react';
import {doc,getDoc,setDoc} from 'firebase/firestore';
import {db} from './firebase';

export const VIEW_DEFAULTS={journal:'gallery',cards:'deck'};
const valid=(kind,value)=>kind==='journal'?['gallery','list'].includes(value):kind==='cards'&&['deck','list'].includes(value);
const key=(uid,kind)=>`tradelog-${kind==='journal'?'journal':'card'}-layout:${uid}`;
const pendingKey=uid=>`tradelog-view-pending:${uid}`;
export function readViewLayout(uid,kind){try{const value=localStorage.getItem(key(uid,kind));return valid(kind,value)?value:VIEW_DEFAULTS[kind];}catch{return VIEW_DEFAULTS[kind];}}
export function saveViewLayout(uid,kind,value){if(!valid(kind,value))return;try{localStorage.setItem(key(uid,kind),value);}catch{}}
const cached=uid=>Object.fromEntries(Object.keys(VIEW_DEFAULTS).map(kind=>[kind,readViewLayout(uid,kind)]));
function pending(uid){try{const value=JSON.parse(localStorage.getItem(pendingKey(uid))||'{}');return Object.fromEntries(Object.entries(value).filter(([kind,choice])=>valid(kind,choice)));}catch{return {};}}
function rememberPending(uid,patch){try{localStorage.setItem(pendingKey(uid),JSON.stringify(patch));}catch{}}

// Private account preferences, with an immediate device cache. A late initial
// read or save can never undo a fresh choice or touch the next signed-in user.
export function useWorkspaceViewPreferences(uid){
  const [state,setState]=useState(()=>({uid,layouts:cached(uid),status:''}));
  const session=useRef(null);
  const write=useCallback((scope,patch)=>{
    const versions={};
    for(const [kind,value] of Object.entries(patch)){scope.pending[kind]=value;versions[kind]=(scope.versions[kind]||0)+1;scope.versions[kind]=versions[kind];}
    rememberPending(scope.uid,scope.pending);
    if(scope.active)setState(previous=>({...previous,status:'saving'}));
    setDoc(doc(db,'users',scope.uid,'meta','workspaceViews'),patch,{merge:true}).then(()=>{
      if(!scope.active||session.current!==scope)return;
      for(const kind of Object.keys(patch))if(scope.versions[kind]===versions[kind])delete scope.pending[kind];
      rememberPending(scope.uid,scope.pending);
      if(scope.active&&session.current===scope)setState(previous=>({...previous,status:Object.keys(scope.pending).length?'saving':'saved'}));
    }).catch(()=>{if(scope.active&&session.current===scope&&Object.keys(patch).some(kind=>scope.versions[kind]===versions[kind]&&Object.hasOwn(scope.pending,kind)))setState(previous=>({...previous,status:'local'}));});
  },[]);
  useEffect(()=>{
    const scope={uid,active:true,touched:new Set(),pending:uid?pending(uid):{},versions:{}};session.current=scope;
    const resume={...scope.pending};
    setState({uid,layouts:{...cached(uid),...scope.pending},status:''});
    if(uid)getDoc(doc(db,'users',uid,'meta','workspaceViews')).then(snapshot=>{
      if(!scope.active)return;
      const saved=snapshot.exists()?snapshot.data():{};
      setState(previous=>{
        const layouts={...previous.layouts};
        for(const kind of Object.keys(VIEW_DEFAULTS))if(valid(kind,saved[kind])&&!scope.touched.has(kind)&&!Object.hasOwn(resume,kind)){layouts[kind]=saved[kind];saveViewLayout(uid,kind,saved[kind]);}
        return {...previous,layouts};
      });
    }).catch(()=>{}).finally(()=>{const retry=Object.fromEntries(Object.entries(resume).filter(([kind])=>!scope.touched.has(kind)));if(scope.active&&Object.keys(retry).length)write(scope,retry);});
    return()=>{scope.active=false;};
  },[uid,write]);
  const choose=useCallback((kind,value)=>{
    if(!uid||!valid(kind,value))return;
    const scope=session.current;if(scope?.uid!==uid||!scope.active)return;
    scope.touched.add(kind);saveViewLayout(uid,kind,value);
    setState(previous=>({...previous,layouts:{...previous.layouts,[kind]:value}}));write(scope,{[kind]:value});
  },[uid,write]);
  return {layouts:state.uid===uid?state.layouts:cached(uid),status:state.uid===uid?state.status:'',choose};
}

export function useViewLayout(uid,kind,controlled,onChange){
  const [local,setLocal]=useState(()=>({uid,value:readViewLayout(uid,kind)}));
  const value=valid(kind,controlled)?controlled:local.uid===uid?local.value:readViewLayout(uid,kind);
  const choose=choice=>{if(!valid(kind,choice))return;if(onChange)onChange(choice);else{saveViewLayout(uid,kind,choice);setLocal({uid,value:choice});}};
  return [value,choose];
}
